import { MoreHorizontal } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { AppLink } from "../primitives/link";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../primitives/menu";
import { CountBadge } from "../primitives/tabs";

/** The main column of every page: content padding and the max width (ui-spec §2.1). */
export function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "nd-page mx-0 w-full max-w-[var(--nd-content-max-width)] px-[var(--nd-content-padding-x)] pt-[var(--nd-content-padding-top)] pb-12",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface Crumb {
  label: string;
  href?: string;
}

/** The muted `Parent /` links that lead a page title (`Runs / New job`, `Storage / datasets`). */
export function TitleCrumbs({ items }: { items: Crumb[] }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 shrink items-center">
      <ol className="flex min-w-0 items-center gap-2 text-page-title text-fg-muted">
        {items.map((c, i) => (
          <li key={`${c.label}-${i}`} className="flex min-w-0 items-center gap-2">
            {c.href ? (
              <AppLink href={c.href} className="truncate hover:text-fg-primary">
                {c.label}
              </AppLink>
            ) : (
              <span className="truncate">{c.label}</span>
            )}
            <span aria-hidden>/</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Page title row (ui-spec §3.1, dashboard theme): optional parent crumbs, the title, then actions on the right with
 * the one primary last. The page's CLI command rides on the heading for Shift-C; no visible button.
 */
export function PageHeader({
  title,
  parents,
  cli,
  actions,
  meta,
  description,
  children,
}: {
  title: React.ReactNode;
  /** Muted `Parent /` links before the title. */
  parents?: Crumb[];
  cli?: string;
  actions?: React.ReactNode;
  /** One muted line of facts under the title (a status, a version); never prose. */
  meta?: React.ReactNode;
  /** The admin console's one-line page description; drawn like `meta`. */
  description?: React.ReactNode;
  /** Section tabs, drawn under the title row. */
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("nd-page-header", children ? "mb-4" : "mb-5")}>
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-3">
        <div className="nd-page-heading flex min-w-0 items-center gap-2">
          {parents ? <TitleCrumbs items={parents} /> : null}
          <h1 data-copy-cli={cli} className="truncate text-page-title font-semibold text-fg-primary">
            {title}
          </h1>
        </div>
        {actions ? <div className="nd-page-actions flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {meta || description ? <div className="nd-page-description mt-1 flex flex-wrap items-center gap-x-2 text-ui text-fg-muted">{meta ?? description}</div> : null}
      {children ? <div className="nd-page-tabs mt-3">{children}</div> : null}
    </div>
  );
}

/** `Compute / LoRA-fine-tune`; more than four segments collapse the middle into a … menu (ui-spec §2.5). */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  const collapsed = items.length > 4;
  const visible = collapsed ? [items[0]!, ...items.slice(-2)] : items;
  const hidden = collapsed ? items.slice(1, -2) : [];
  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex flex-wrap items-center gap-3 text-ui">
        {visible.map((c, i) => {
          const last = i === visible.length - 1;
          return (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 ? (
                <li aria-hidden className="text-fg-muted">
                  /
                </li>
              ) : null}
              {collapsed && i === 1 ? (
                <>
                  <li>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="text-fg-secondary hover:text-fg-primary" aria-label="More breadcrumbs">
                        …
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {hidden.map((h) => (
                          <DropdownMenuItem key={h.label} asChild>
                            <AppLink href={h.href ?? "#"}>{h.label}</AppLink>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                  <li aria-hidden className="text-fg-muted">
                    /
                  </li>
                </>
              ) : null}
              <li>
                {last || !c.href ? (
                  <span className="text-fg-muted" aria-current={last ? "page" : undefined}>
                    {c.label}
                  </span>
                ) : (
                  <AppLink href={c.href} className="text-fg-secondary hover:text-fg-primary">
                    {c.label}
                  </AppLink>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/** A body section: a quiet title left with an optional count, provenance or a link right (P11). */
export function Section({
  title,
  count,
  aside,
  children,
  className,
}: {
  title: string;
  count?: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className={cn("nd-section mt-[var(--nd-section-gap)] first:mt-0 [.grid>&]:mt-0", className)}>
      <div className="nd-section-header mb-[var(--nd-section-header-gap)] flex min-h-7 flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 id={id} className="flex items-center gap-2 text-section-title font-semibold text-fg-primary">
          {title}
          {count !== undefined ? <CountBadge count={count} /> : null}
        </h2>
        {aside ? <div className="text-small text-fg-muted">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

export interface MenuAction {
  label: string;
  onSelect: () => void;
  shortcut?: string;
  destructive?: boolean;
  disabled?: boolean;
  /** Tooltip-free reason shown in the label when disabled (for example the missing role). */
  separatorBefore?: boolean;
}

/** The ghost `⋯` Actions button at the small control size (shortcut "."). */
export function ActionsMenu({ actions, label = "Actions" }: { actions: MenuAction[]; label?: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-actions-menu
          className="nd-above inline-flex size-[var(--nd-control-height-sm)] items-center justify-center rounded-md text-fg-muted hover:bg-ghost-hover hover:text-fg-primary data-[state=open]:bg-ghost-hover data-[state=open]:text-fg-primary"
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {actions.map((a) => (
          <React.Fragment key={a.label}>
            {a.separatorBefore ? <div role="separator" className="-mx-1 my-1 h-px bg-line-subtle" /> : null}
            <DropdownMenuItem destructive={a.destructive} disabled={a.disabled} onSelect={a.onSelect}>
              {a.label}
              {a.shortcut ? <span className="ml-auto font-mono text-small text-fg-muted">{a.shortcut}</span> : null}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
